import { Stack, Typography, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import { MembershipType } from '../types/api/entreprise'
import ReadOnlyField from './common/fields/readOnlyField'
import SectionForm from './common/sectionForm'
import React, { useMemo } from 'react'
import { addUniqueKeyToList, getDayAndMonthNameYear } from '../utils'
import { uniqueKey } from '../constants'
import { t } from 'i18next'
interface Props {
  data: MembershipType | undefined
}
interface TypographyProps {
  children: React.ReactNode
}
const TypographyComponent = ({ children }: TypographyProps) => {
  return <Typography component='span'>{children}</Typography>
}
const returnEmptyIfNull = (value: string | undefined | null) => (value ? value : <>&nbsp;</>)

const MembershipTab = ({ data }: Props) => {
  const isMobile = useMediaQuery('(max-width:800px)')
  const isSmallMobile = useMediaQuery('(max-width:480px)')
  const referrals = useMemo(() => addUniqueKeyToList(data?.referrals), [data?.referrals])
  return (
    <Stack spacing={isSmallMobile ? '32px' : isMobile ? '40px' : '53px'} mt={isSmallMobile ? '24px' : isMobile ? '36px' : '48px'}>
      <SectionForm title='Adhésion'>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? '24px' : '0'}>
          <Box sx={{ width: isMobile ? '100%' : '48%', mr: isMobile ? '0' : '5%', display: 'flex' }}>
            <Stack spacing={isSmallMobile ? '20px' : isMobile ? '28px' : '34px'} sx={{ flex: 1, mr: isMobile ? '0' : '5%' }}>
              <ReadOnlyField
                label='Adhésion'
                value={returnEmptyIfNull(
                  data?.membership?.membership_date
                    ? getDayAndMonthNameYear(data.membership.membership_date)
                    : null,
                )}
              />
              <ReadOnlyField
                label='Date de renouvellement'
                value={returnEmptyIfNull(
                  data?.membership?.renewal_date
                    ? getDayAndMonthNameYear(data.membership.renewal_date)
                    : null,
                )}
              />
              <ReadOnlyField
                label={t('pages.entrepriseProfile.general.membership.nummeber_provincial')}
                value={returnEmptyIfNull(data?.membership?.provincial_membership_number)}
              />
            </Stack>
            <Stack spacing={isSmallMobile ? '20px' : isMobile ? '28px' : '34px'} sx={{ flex: 1 }}>
              <ReadOnlyField label='Statut' value={returnEmptyIfNull(data?.membership?.status)} />
              <ReadOnlyField
                label='Catégorie de membre'
                value={returnEmptyIfNull(data?.membership?.company_category)}
              />
              <ReadOnlyField
                label={t('pages.entrepriseProfile.general.membership.nummeber_regional')}
                value={returnEmptyIfNull(data?.membership?.regional_membership_number)}
              />
            </Stack>
          </Box>
          <Box sx={{ width: isMobile ? '100%' : '40%', mt: isMobile ? '24px' : '0' }}>
            <ReadOnlyField
              label='Succursale ACQ régionale'
              value={
                <Stack>
                  {data?.acq_regional_branch?.name && (
                    <TypographyComponent>{data.acq_regional_branch.name}</TypographyComponent>
                  )}
                  {data?.acq_regional_branch?.phone_number && (
                    <TypographyComponent>
                      {data.acq_regional_branch.phone_number}
                    </TypographyComponent>
                  )}
                  {data?.acq_regional_branch?.email && (
                    <TypographyComponent>{data.acq_regional_branch.email}</TypographyComponent>
                  )}
                  {(data?.acq_regional_branch?.civic_number || data?.acq_regional_branch?.address) && (
                    <TypographyComponent>
                      {[data.acq_regional_branch.civic_number, data.acq_regional_branch.address]
                        .filter(Boolean)
                        .join(' ')}
                    </TypographyComponent>
                  )}
                  {data?.acq_regional_branch?.office && (
                    <TypographyComponent>{data.acq_regional_branch.office}</TypographyComponent>
                  )}
                  {(data?.acq_regional_branch?.city || data?.acq_regional_branch?.province || data?.acq_regional_branch?.postal_code) && (
                    <TypographyComponent>
                      {[data.acq_regional_branch.city, data.acq_regional_branch.province, data.acq_regional_branch.postal_code]
                        .filter(Boolean)
                        .join(', ')}
                    </TypographyComponent>
                  )}
                </Stack>
              }
            />
          </Box>
        </Stack>
      </SectionForm>
      {data?.referrals && data?.referrals?.length > 0 && (
        <SectionForm title='entreprise(s) référé(s)'>
          <Stack
            spacing={isSmallMobile ? '12px' : '16px'}
            sx={{
              'div:last-child': {
                borderBottom: 'none',
                pb: '0',
              },
            }}
          >
            {referrals.map((referral) => (
              <Stack
                key={referral[uniqueKey]}
                direction={isMobile ? 'column' : 'row'}
                spacing={isMobile ? '16px' : '0'}
                sx={{
                  width: '100%',
                  pb: isSmallMobile ? '16px' : isMobile ? '20px' : '26px',
                  borderBottom: '1px solid #DADCDD',
                }}
              >
                <Box sx={{ width: isMobile ? '100%' : '26%' }}>
                  <ReadOnlyField
                    label='No du référé'
                    value={returnEmptyIfNull(referral?.provincial_membership_number)}
                  />
                </Box>
                <Box sx={{ width: isMobile ? '100%' : '26%' }}>
                  <ReadOnlyField
                    label='Succursale ACQ régionale'
                    value={returnEmptyIfNull(referral?.acq_provincial_name)}
                  />
                </Box>
              </Stack>
            ))}
          </Stack>
        </SectionForm>
      )}
      {data?.referred_by && (
        <SectionForm title='entreprise référante'>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? '16px' : '0'}
            sx={{ width: '100%' }}
          >
            <Box sx={{ width: isMobile ? '100%' : '26%' }}>
              <ReadOnlyField
                label='No du référant'
                value={returnEmptyIfNull(data?.referred_by?.provincial_membership_number)}
              />
            </Box>
            <Box sx={{ width: isMobile ? '100%' : '26%' }}>
              <ReadOnlyField
                label='Succursale ACQ régionale'
                value={returnEmptyIfNull(data?.referred_by?.acq_provincial_name)}
              />
            </Box>
          </Stack>
        </SectionForm>
      )}
    </Stack>
  )
}

export default MembershipTab
